import { useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PricingTier } from "../PropertySubscriptionContext";
import { apiClient } from "@/api/client";
import { logger } from "@/utils/debugLogger";
import { storyQueryKeys } from "@/queries/storyQueryKeys";

interface UsePricingTiersParams {
  isAuthenticated: boolean;
  accessToken: string | null;
  sessionKey: string;
  getStoredAccessToken: () => string | null;
}

export interface PricingTiersResult {
  pricingTiers: PricingTier[];
  loadPricingTiers: () => Promise<void>;
}

export function usePricingTiers({
  isAuthenticated,
  accessToken,
  sessionKey,
  getStoredAccessToken,
}: UsePricingTiersParams): PricingTiersResult {
  const query = useQuery<PricingTier[], Error>({
    queryKey: storyQueryKeys.package.pricingTiers(sessionKey),
    enabled: false,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const response = await apiClient.get("/api/users/pricing-tiers/");
      return response.data.pricing_tiers ?? [];
    },
  });

  useEffect(() => {
    if (!query.error) return;
    logger.info(
      { component: "PropertySubscriptionContext", action: "loadPricingTiers" },
      "Pricing tiers unavailable; continuing with existing defaults",
      query.error,
    );
  }, [query.error]);

  const { refetch } = query;
  const loadPricingTiers = useCallback(async () => {
    if (!isAuthenticated || !accessToken || !getStoredAccessToken()) return;
    await refetch();
  }, [accessToken, getStoredAccessToken, isAuthenticated, refetch]);

  return { pricingTiers: query.data ?? [], loadPricingTiers };
}
